import { Can } from "@casl/react";
import { useAbility } from "@via/context/ability-context";
import { classNames } from "@via/utils/helpers";
import { AppViewType } from "common";
import { useState } from "react";
import { useDebounceCallback, useDebounceValue } from "usehooks-ts";

type FilterItem = { id: string; value: unknown };

type WorkbenchsFilterProps = {
  columnFilters: FilterItem[];
  setColumnFilters: (
    filters: (prevFilters: FilterItem[]) => FilterItem[]
  ) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
};

const selectedFilterClass =
  "bg-indigo-600 text-white hover:bg-indigo-500  hover:shadow-md";
const unSelectedFilterClass = "bg-white";
const leftButtonClass = "rounded-l-md";
const middleButtonClass = "-ml-px";
const rightButtonClass = "rounded-r-md -ml-px";
const defaultButtonClass =
  "relative inline-flex h-9 items-center px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10";

const WorkbenchsFilter = ({
  columnFilters,
  setColumnFilters,
  setPage,
  setPageSize,
}: WorkbenchsFilterProps) => {
  const ability = useAbility([
    {
      action: "View",
      subject: "Button",
      condition: false,
    },
  ]);

  const [selectedFilterType, setSelectedFilterType] = useState(
    columnFilters.find(filter => filter.id === "type")?.value ||
      AppViewType.SharedWithMe
  );

  const [debouncedNameFilter] = useDebounceValue(
    (columnFilters.find(filter => filter.id === "name")?.value as string) || "",
    300
  );

  const [debouncedOwnerFilter] = useDebounceValue(
    (columnFilters.find(filter => filter.id === "owner")?.value as string) ||
      "",
    300
  );

  const debounced = useDebounceCallback((id, value) => {
    setColumnFilters(prevFilters => {
      const updatedFilters = prevFilters.map(filter =>
        filter.id === id ? { ...filter, value } : filter
      );
      if (!prevFilters.find(filter => filter.id === id) && value !== "") {
        return [...updatedFilters, { id, value }];
      }
      return updatedFilters.filter(filter => filter.value !== "");
    });
    setPage(0);
    setPageSize(10);
  }, 300);

  const handleSelectViewType = (type: number) => {
    setSelectedFilterType(type);
    debounced("type", type);
  };

  return (
    <div className="w-full my-2 flex flex-col items-baseline justify-between gap-2 md:flex-row">
      <div className="w-full flex-1">
        <input
          type="text"
          placeholder="Filter By Name"
          className="w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          defaultValue={debouncedNameFilter}
          onChange={e => debounced("name", e.target.value)}
        />
      </div>
      <div className="w-full flex-1">
        <input
          type="text"
          placeholder="Filter By Owner"
          className="w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          defaultValue={debouncedOwnerFilter}
          onChange={e => debounced("owner", e.target.value)}
        />
      </div>
      <span className="isolate inline-flex rounded-md shadow-sm">
        <button
          type="button"
          onClick={() => handleSelectViewType(AppViewType.MyApps)}
          className={classNames(
            defaultButtonClass,
            leftButtonClass,
            selectedFilterType === AppViewType.MyApps
              ? selectedFilterClass
              : unSelectedFilterClass
          )}>
          My Workbenches
        </button>
        <button
          type="button"
          onClick={() => handleSelectViewType(AppViewType.SharedWithMe)}
          className={classNames(
            defaultButtonClass,
            ability.can("View", "Button")
              ? middleButtonClass
              : rightButtonClass,
            selectedFilterType === AppViewType.SharedWithMe
              ? selectedFilterClass
              : unSelectedFilterClass
          )}>
          Shared With Me
        </button>
        <Can I="View" a="Button" ability={ability}>
          <button
            type="button"
            onClick={() => handleSelectViewType(AppViewType.AdminView)}
            className={classNames(
              defaultButtonClass,
              rightButtonClass,
              selectedFilterType === AppViewType.AdminView
                ? selectedFilterClass
                : unSelectedFilterClass
            )}>
            Admin View
          </button>
        </Can>
      </span>
    </div>
  );
};

export default WorkbenchsFilter;
